// Call classification — AI-filled (≥ threshold) or staff-set dropdowns, tags, workflow fields, history.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bot, History, Loader2, Tags, UserCheck, X } from "lucide-react";
import api from "../../lib/api";
import { T2Panel, T2Label } from "../quotes/_t2";

let cfgCache = null;
export const loadCallConfig = async () => (cfgCache ||= (await api.get("/calls/classification/config")).data);
export const clearCallConfig = () => { cfgCache = null; };

function Badge({ c }) {
  if (!c) return null;
  if (c.source === "staff") return <span className="cc-badge staff" title={`Set by ${c.by || "staff"}`}><UserCheck className="w-2.5 h-2.5" /> staff</span>;
  if (c.needs_review) return <span className="cc-badge review" title={`AI unsure (${Math.round((c.confidence || 0) * 100)}%)${c.suggestion ? ` — suggested: ${[].concat(c.suggestion).join(", ")}` : ""}`}>needs review</span>;
  if (c.source === "ai") return <span className="cc-badge ai" title="AI confidence"><Bot className="w-2.5 h-2.5" /> {Math.round((c.confidence || 0) * 100)}%</span>;
  return null;
}

function FieldInput({ f, c, onSave }) {
  const v = c?.value;
  if (f.multi) {
    const vals = v || [];
    return (
      <div className="flex flex-wrap gap-1">
        {f.options.map((o) => (
          <button key={o} type="button" onClick={() => onSave(vals.includes(o) ? vals.filter((x) => x !== o) : [...vals, o])}
            className={`cc-chip${vals.includes(o) ? " on" : ""}`} data-testid={`cc-${f.key}-${o}`}>{o}</button>
        ))}
      </div>
    );
  }
  return (
    <select value={v || ""} onChange={(e) => onSave(e.target.value || null)} className="t2-input" style={{ fontSize: 12, padding: "5px 8px" }} data-testid={`cc-select-${f.key}`}>
      <option value="">{c?.needs_review && c?.suggestion ? `— Review (AI suggests: ${c.suggestion}) —` : "— Not set —"}</option>
      {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

export default function CallClassifyPanel({ doc, onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [users, setUsers] = useState([]);
  const [state, setState] = useState({ cls: doc.CallClass || {}, tags: doc.CallTags || [], ops: doc.CallOps || {}, hist: doc.ClassHistory || [] });
  const [newTag, setNewTag] = useState("");
  const [busy, setBusy] = useState(false);
  const [showHist, setShowHist] = useState(false);
  useEffect(() => { loadCallConfig().then(setCfg).catch(() => {}); api.get("/users").then(({ data }) => setUsers(data || [])).catch(() => {}); }, []);
  useEffect(() => { setState({ cls: doc.CallClass || {}, tags: doc.CallTags || [], ops: doc.CallOps || {}, hist: doc.ClassHistory || [] }); }, [doc]);

  const save = async (body) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/calls/${doc._id}/classify`, body);
      setState({ cls: data.CallClass || {}, tags: data.CallTags || [], ops: data.CallOps || {}, hist: data.ClassHistory || [] });
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };
  if (!cfg) return null;
  const { cls, tags, ops, hist } = state;
  const review = cfg.fields.filter((f) => cls[f.key]?.needs_review).length;
  const addTag = () => { const t = newTag.trim().toLowerCase(); if (t) save({ tags: [...tags, t] }); setNewTag(""); };
  const parked = cls.status?.value === "Parked";
  return (
    <T2Panel icon={Tags} title="Classification" testid="panel-classification"
      right={<span className="t2-mono" style={{ fontSize: 10, color: review ? "var(--t2-brass-ink)" : "var(--t2-grey3)" }} data-testid="cc-review-count">
        {busy && <Loader2 className="w-3 h-3 animate-spin inline mr-1" />}{review ? `${review} need review` : `AI ≥ ${Math.round(cfg.threshold * 100)}% only`}</span>}>
      <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}>
        {cfg.fields.map((f) => (
          <div key={f.key} data-testid={`cc-field-${f.key}`} style={f.multi ? { gridColumn: "1 / -1" } : undefined}>
            <div className="flex items-center gap-2 mb-1"><T2Label>{f.label}</T2Label><Badge c={cls[f.key]} /></div>
            <FieldInput f={f} c={cls[f.key]} onSave={(val) => {
              if (f.key === "status" && val === "Parked" && !(ops.park_reason || "").trim()) {
                const r = window.prompt("Park reason (required)");
                if (!r || !r.trim()) return;
                save({ fields: { status: val }, park_reason: r.trim() });
                return;
              }
              save({ fields: { [f.key]: val } });
            }} />
          </div>
        ))}
      </div>
      <div className="grid gap-3 mt-4 pt-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", borderTop: "1px solid var(--t2-border)" }}>
        <label><T2Label>Assigned to</T2Label>
          <select value={ops.assigned_to || ""} onChange={(e) => save({ assigned_to: e.target.value })} className="t2-input" style={{ fontSize: 12, padding: "5px 8px" }} data-testid="cc-assigned-to">
            <option value="">— Nobody —</option>{users.map((u) => <option key={u.id} value={u.name || u.email}>{u.name || u.email}</option>)}
          </select></label>
        <label><T2Label>Follow-up due</T2Label>
          <input type="date" value={ops.follow_up_due || ""} onChange={(e) => save({ follow_up_due: e.target.value })} className="t2-input" style={{ fontSize: 12, padding: "4px 8px" }} data-testid="cc-follow-up" /></label>
        <label><T2Label>Escalation owner</T2Label>
          <select value={ops.escalation_owner || ""} onChange={(e) => save({ escalation_owner: e.target.value })} className="t2-input" style={{ fontSize: 12, padding: "5px 8px" }} data-testid="cc-escalation">
            <option value="">— None —</option>{users.map((u) => <option key={u.id} value={u.name || u.email}>{u.name || u.email}</option>)}
          </select></label>
        <label><T2Label>Park reason{parked ? " *" : ""}</T2Label>
          <input defaultValue={ops.park_reason || ""} key={ops.park_reason || ""} onBlur={(e) => e.target.value !== (ops.park_reason || "") && save({ park_reason: e.target.value })}
            placeholder={parked ? "Required while parked" : "Why is it parked?"} className="t2-input" style={{ fontSize: 12, padding: "4px 8px" }} data-testid="cc-park-reason" /></label>
      </div>
      <div className="mt-4 pt-3" style={{ borderTop: "1px solid var(--t2-border)" }}>
        <T2Label>Tags</T2Label>
        <div className="flex flex-wrap items-center gap-1.5 mt-1" data-testid="cc-tags">
          {tags.map((t) => (
            <span key={t} className={`cc-tag${t.startsWith("ai:") ? " ai" : ""}`} data-testid={`cc-tag-${t}`}>
              {t.startsWith("ai:") && <Bot className="w-2.5 h-2.5" />}{t.replace(/^ai:/, "")}
              <button type="button" onClick={() => save({ tags: tags.filter((x) => x !== t) })} aria-label="Remove tag"><X className="w-2.5 h-2.5" /></button>
            </span>
          ))}
          <input value={newTag} onChange={(e) => setNewTag(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTag()} placeholder="+ add tag"
            className="t2-input" style={{ fontSize: 11, padding: "3px 8px", width: 110 }} data-testid="cc-tag-input" />
        </div>
      </div>
      {hist.length > 0 && (
        <div className="mt-3">
          <button type="button" onClick={() => setShowHist(!showHist)} className="t2-mono" style={{ fontSize: 10, color: "var(--t2-grey3)" }} data-testid="cc-history-toggle">
            <History className="w-3 h-3 inline mr-1" />{showHist ? "Hide" : "Show"} change history ({hist.length})
          </button>
          {showHist && (
            <div className="mt-1 space-y-0.5" data-testid="cc-history">
              {[...hist].reverse().slice(0, 30).map((h, i) => (
                <div key={i} className="t2-mono" style={{ fontSize: 10.5, color: "var(--t2-grey2)" }}>
                  {new Date(h.at).toLocaleString("en-GB")} · {h.by} · {h.field}: {[].concat(h.from ?? "—").join(", ")} → {[].concat(h.to ?? "—").join(", ")}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </T2Panel>
  );
}
